import {Module} from "vuex";
import * as firebase from "firebase/app";
import "firebase/firestore";
import {serializer} from "@/api/shared/serializer";
import {EventLocations} from "@/enums/EventLocations";
import { office365Service } from "@/services/office365Service";
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';

const eventLocationModule: Module<any, any> = {
    namespaced: false,
    state: {
        eventLocations: [],
        eventWings: []
    },
    mutations: {
        setEventLocations(state, data) {
            state.eventLocations = data;
        },
        setEventWings(state, data) {
            state.eventWings = data;
        }
    },
    actions: {
      async getEventLocations({ commit, dispatch }) {
          const querySnapshot = await firebase
            .firestore()
            .collection("eventLocations")
            .where("facilityId", "==", this.state.authModule.currentFacility)
            .get();

          let locations: any[] = serializer.querySnapshotToDocuments(querySnapshot);

          if (this.state.authModule.currentFacilityData?.isEnableRoomBooking === true) {
            const msRooms = await dispatch('getMsRooms', { facilityId: this.state.authModule.currentFacility });

            if (msRooms?.length > 0) {
              locations = locations.map(location => ({
                ...location,
                isInOffice365: office365Service.doesLocationMatchOffice365Room(location, msRooms)
              }));
            }
          }

          commit("setEventLocations", locations);
          return locations;
        },
        async getEventWings({commit}) {
          return await firebase
                .firestore()
                .collection("eventLocations")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("type", "==", EventLocations.Wing)
                .get()
                .then((querySnapshot) => {
                    const data = serializer.querySnapshotToDocuments(querySnapshot);
                    commit("setEventWings", data)
                    return data
                }).catch((e) => {
                    commit("setEventWings", [])
                })
        },
        async addEventLocation({dispatch}, form) {
        form.orgId = this.state.authModule.currentFacilityData.orgId
        form.createdById = this.state.authModule.user.id
        form.createdByType = UserTypeEnum.Staff
        form.typesOfRecordChanged = [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.GroupingsAndLocs}`]
          return await firebase
                .firestore()
                .collection("eventLocations")
                .add(form)
                .then((res) => {
                    dispatch("getEventLocations");
                    return res
                });
        },
        async updateEventLocation({dispatch}, form) {
            if (form.type === EventLocations.Wing) {

                if(form?.isDefault === true) return

                await firebase
                    .firestore()
                    .collection("eventLocations")
                    .doc(form.id)
                    .update({
                        'name': form.name,
                        'key': form?.key,
                        'type': form.type,
                        'color': form?.color,
                        'careLevelId': form?.careLevelId || null,
                        'careLevelName': form?.careLevelName || null,
                        'isDisplayResidentDetails': form?.isDisplayResidentDetails || false,
                        'isDisplayResidentDetailsInCommunityTile': form?.isDisplayResidentDetailsInCommunityTile || false,
                        'updatedById': this.state.authModule.user.id,
                        'updatedByType': UserTypeEnum.Staff,
                        'typesOfRecordChanged': [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.GroupingsAndLocs}`]
                    })
                    .then(() => {
                        dispatch("getEventLocations");
                    })
            } else {
                await firebase
                    .firestore()
                    .collection("eventLocations")
                    .doc(form.id)
                    .update({
                        'name': form.name,
                        'key': form?.key,
                        'type': form.type,
                        'color': firebase.firestore.FieldValue.delete(),
                        'isDifferentName': form?.isDifferentName || false,
                        'differentName': form?.differentName || null,
                        'office365Email': form?.office365Email || null,
                        'careLevelId': null,
                        'careLevelName': null,
                        'updatedById': this.state.authModule.user.id,
                        'updatedByType': UserTypeEnum.Staff,
                        'typesOfRecordChanged': [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.GroupingsAndLocs}`]
                    })
                    .then(() => {
                        dispatch("deleteWingIdFromEvents", form.id);
                        dispatch("getEventLocations");
                    })
            }
        },
        async deleteEventLocation({dispatch}, location) {

          const ref = firebase.firestore().collection("eventLocations").doc(location.id)
          
          await ref.update({
            deletedById: this.state.authModule.user.id,
            deletedByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.GroupingsAndLocs}`]
          })

          await ref.delete().then(async () => {
              if (location.type === EventLocations.Wing) {
                  const residents = (await firebase.firestore().collection('residents').where('wingId', '==', location.id).get())
                  residents.docs.forEach(async doc => {
                      await doc.ref.update({wingId: null, wingName: null})
                  });

                  await dispatch("deleteWingIdFromEvents", location.id);
                  await dispatch("deleteWingIdFromCareLevels", location.id);
              }
              dispatch("getEventLocations");
          });
        },
        async deleteWingIdFromEvents(_, locationId) {
          const events = (await firebase.firestore().collection('calender')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where('wingsIds', 'array-contains', locationId).get())
          events.docs.forEach(async doc => {
            await doc.ref.update({
              wingsIds: firebase.firestore.FieldValue.arrayRemove(locationId),
              updatedById: this.state.authModule.user.id, 
              updatedByType: UserTypeEnum.Staff,
              typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.GroupingsAndLocs}`]
            })
          });
        },
        async deleteWingIdFromCareLevels(_, locationId) {

            const careLevels = (await firebase.firestore().collection('careLevels')
            .where("facilityId", "==", this.state.authModule.currentFacility)
            .where('wingsIds', 'array-contains', locationId).get())

            careLevels.docs.forEach(async doc => {
              await doc.ref.update({
                wingsIds: firebase.firestore.FieldValue.arrayRemove(locationId),
                updatedById: this.state.authModule.user.id, 
                updatedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.GroupingsAndLocs}`]
              })
            });
          }
    }
};

export default eventLocationModule;
